import type { SepaPaymentAdminProvider } from '@repo/api';
import {
  ACUBE_PAYMENTS_SORTABLE_FIELDS,
  SEPA_PAYMENTS_ADMIN_SORTABLE_FIELDS,
  isValidDateString,
} from '@repo/utils';
import type {
  FetchAcubePaymentTabCountsParams,
  FetchAcubePaymentsParams,
} from '@/api/acube-payments/models';
import type { FetchSepaPaymentsParams } from '@/api/sepa-payments/models';
import {
  type ApiParamReaders,
  booleanParam,
  pickApiParams,
  valueParam,
} from '@/lib/helpers/list-api-params';
import type { AcubePaymentsSearch } from '@/routes/-shared/acubePaymentsSearchSchema';
import type { AcubePaymentTabFilter } from './payment-tabs';

/** Testo con almeno un carattere che non sia uno spazio. */
const nonBlankTextParam = (value: string | null | undefined) =>
  value?.trim() ? value : undefined;

const dateOnlyParam = (value: string | null | undefined) =>
  value && isValidDateString(value) ? value : undefined;

/**
 * Provider della lista "senza specchio / non ACube". `@repo/api` si importa
 * solo come tipo (CommonJS): la lista è ripetuta qui, e `satisfies` la tiene
 * allineata al tipo del backend.
 */
export const SEPA_ADMIN_PROVIDERS = [
  'FABRICK',
  'ACUBE_UNMIRRORED',
  'UNKNOWN',
] as const satisfies readonly SepaPaymentAdminProvider[];

const SORT_SEPARATOR = '|';

/** Tiene l'ordinamento solo se ogni campo è ordinabile nella lista. */
export function pickSupportedSort(
  sort: unknown,
  sortableFields: readonly string[]
): string | undefined {
  if (typeof sort !== 'string' || !sort) return undefined;
  const fields = sort
    .split(',')
    .map((part) => part.split(SORT_SEPARATOR)[0]?.trim());
  return fields.every((field) => !!field && sortableFields.includes(field))
    ? sort
    : undefined;
}

type AcubeApiParams = Omit<FetchAcubePaymentsParams, 'skip' | 'take'>;
type SepaApiParams = Omit<FetchSepaPaymentsParams, 'skip' | 'take'>;

/**
 * Le due liste leggono lo stesso URL ma parlano con DTO diversi, che
 * rifiutano con 400 i parametri sconosciuti o non validi: ognuna prende solo
 * le chiavi che il proprio endpoint conosce (il `direction` dello specchio
 * non arriva alla lista SEPA, `subTab` non arriva a nessuna) e solo con un
 * valore che il DTO accetta (gli enum li valida lo schema della rotta). Un
 * URL scritto a mano o vecchio perde il filtro invece di rompere la tabella.
 */
const ACUBE_COMMON_API_PARAMS: ApiParamReaders<
  AcubePaymentsSearch,
  FetchAcubePaymentTabCountsParams
> = {
  search: nonBlankTextParam,
  tenantId: nonBlankTextParam,
  direction: valueParam,
  dateFrom: dateOnlyParam,
  dateTo: dateOnlyParam,
  // Combobox con id stringa: nell'URL `'true'`/`'false'`.
  linked: booleanParam,
};

const ACUBE_API_PARAMS: ApiParamReaders<AcubePaymentsSearch, AcubeApiParams> = {
  ...ACUBE_COMMON_API_PARAMS,
  sort: (value) => pickSupportedSort(value, ACUBE_PAYMENTS_SORTABLE_FIELDS),
};

const SEPA_API_PARAMS: ApiParamReaders<AcubePaymentsSearch, SepaApiParams> = {
  search: nonBlankTextParam,
  tenantId: nonBlankTextParam,
  provider: valueParam,
  status: valueParam,
  sort: (value) =>
    pickSupportedSort(value, SEPA_PAYMENTS_ADMIN_SORTABLE_FIELDS),
};

export function buildAcubePaymentFilterParams(
  search: AcubePaymentsSearch,
  tabFilter: AcubePaymentTabFilter
): AcubeApiParams {
  return { ...pickApiParams(search, ACUBE_API_PARAMS), ...tabFilter };
}

const TAB_COUNT_API_PARAMS: ApiParamReaders<
  AcubePaymentsSearch,
  FetchAcubePaymentTabCountsParams
> = {
  ...ACUBE_COMMON_API_PARAMS,
  provider: valueParam,
  status: valueParam,
};

/**
 * Filtri dei contatori dei tab: quelli della lista dello specchio senza
 * ordinamento (il DTO dei contatori lo rifiuta con 400) e senza il filtro
 * del tab, che il backend aggiunge per ciascun contatore; più provider e
 * stato di «Altri bonifici», che valgono solo per il suo contatore.
 */
export function buildAcubePaymentTabCountParams(
  search: AcubePaymentsSearch
): FetchAcubePaymentTabCountsParams {
  return pickApiParams(search, TAB_COUNT_API_PARAMS);
}

export function buildSepaPaymentFilterParams(
  search: AcubePaymentsSearch
): SepaApiParams {
  return pickApiParams(search, SEPA_API_PARAMS);
}

/**
 * Parametri da togliere dall'URL quando si apre un tab dell'altra lista: i
 * filtri propri di una lista non devono restare appesi (e contati nel badge
 * "Filtri") nell'altra. Fra tab della stessa lista i filtri restano.
 */
export const ACUBE_ONLY_URL_PARAMS = {
  direction: undefined,
  linked: undefined,
  dateFrom: undefined,
  dateTo: undefined,
} as const;

export const SEPA_ONLY_URL_PARAMS = {
  provider: undefined,
  status: undefined,
} as const;
